"use client"

import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check, Minus } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Binary checkbox (Radix) supporting checked, unchecked and indeterminate states.
 * The Root is the (touch-sized) hit area; the painted 16px box is an inner span
 * styled via `boxClassName` so the visual size stays constant while the tap
 * target grows below `lg`.
 *
 * Caveat: the Root has no width class, so at `lg` and up it's only as wide as
 * its content (the 16px box) if the surrounding layout lets it be — in a plain
 * block context it can stretch to fill the container, giving you a full-width
 * invisible hit area. Give it `w-fit` (or place it in a flex context) when
 * using it outside a grid/flex cell that already constrains its width.
 *
 * @category Forms
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    /** Styles the painted 16px box; `className` styles the (touch-sized) root. */
    boxClassName?: string
  }
>(({ className, boxClassName, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      // Root is the hit area: transparent, grows to the 44px touch floor
      // below lg; the painted box is the inner span so the visual stays 16px.
      "group/checkbox peer grid shrink-0 cursor-pointer place-content-center focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
      "max-lg:min-h-11 max-lg:min-w-11",
      className
    )}
    {...props}
  >
    <span
      className={cn(
        "grid h-4 w-4 place-content-center rounded-sm border border-primary",
        "group-focus-visible/checkbox:ring-2 group-focus-visible/checkbox:ring-primary/25",
        "group-data-[state=checked]/checkbox:bg-primary group-data-[state=checked]/checkbox:text-primary-foreground",
        "group-data-[state=indeterminate]/checkbox:bg-primary group-data-[state=indeterminate]/checkbox:text-primary-foreground",
        boxClassName
      )}
    >
      <CheckboxPrimitive.Indicator className="grid place-content-center text-current">
        <Check className="h-4 w-4 group-data-[state=indeterminate]/checkbox:hidden" />
        <Minus className="hidden h-4 w-4 group-data-[state=indeterminate]/checkbox:block" />
      </CheckboxPrimitive.Indicator>
    </span>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
